import React, { FC, useEffect, useState } from 'react'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { useTranslation } from 'react-i18next'
import { Button, ModalVariables, SearchComponent, StartPage } from '../../../components'
import { staffObjectStorageService } from '../../../api'
import { StaffBucket } from '../../../api/staffObjectStorageService'
import { useUi } from '../../../context/ui.context'
import { usePageTitle } from '../../../context/navigationContext'
import { icons } from '../../../icons'
import { timeConverterMMDDYYHHMM } from '../../../utils/dates'
import { formatFileSize } from '../../../utils/sizes'
import { getObjectStorageError } from './utils'

const Buckets: FC<{ bucketListPath: string }> = ({ bucketListPath }) => {
  const [t] = useTranslation()
  const { viewAlert } = useUi()
  usePageTitle(t('PAGE_TITLE_STAFF_OBJECT_STORAGE'))
  const [page] = useQueryParam('page', NumberParam)
  const [pageSize] = useQueryParam('page_size', NumberParam)
  const [ordering] = useQueryParam('ordering', StringParam)
  const [search] = useQueryParam('search', StringParam)
  const [buckets, setBuckets] = useState<any[]>([])
  const [pagination, setPagination] = useState<any>()
  const [pageLoading, setPageLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [selectedBucket, setSelectedBucket] = useState<StaffBucket | null>(null)
  const [viewModal, setViewModal] = useState(false)

  const loadBuckets = (background = false) => {
    if (!background) setLoading(true)
    staffObjectStorageService.getBuckets({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || '-created_at',
      search: search || '',
    })
      .then(({ data }) => {
        setPagination(data)
        setBuckets(data.objects.map((bucket) => ({
          ...bucket,
          navigate: `${bucketListPath}/${bucket.id}`,
          owner: bucket.object_user.ceph_uid || 'N/A',
          client_name: bucket.client?.display || bucket.client?.name || 'N/A',
          service_name: bucket.service?.display_name || 'N/A',
          usage: formatFileSize(bucket.usage),
          created_at: timeConverterMMDDYYHHMM(bucket.created_at),
        })))
      })
      .catch((error) => viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      }))
      .finally(() => {
        setPageLoading(false)
        setLoading(false)
      })
  }

  useEffect(() => {
    loadBuckets()
  }, [page, pageSize, ordering, search])

  const closeModal = async () => {
    if (!selectedBucket) return
    try {
      setLoading(true)
      await staffObjectStorageService.deleteBucket(selectedBucket.id)
      setViewModal(false)
      setSelectedBucket(null)
      viewAlert({ severity: 'information', message: t('BUCKET_DELETED') })
      loadBuckets(true)
    } catch (error) {
      viewAlert({
        severity: 'error',
        message: getObjectStorageError(error, t('ERROR_OCCURRED')),
      })
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    { title: t('NAME'), key: 'name', sorted: true },
    { title: t('VISIBILITY'), key: 'access', sorted: true },
    { title: t('OBJECT_STORAGE_USER'), key: 'owner' },
    { title: t('CLIENT'), key: 'client_name' },
    { title: t('SERVICE'), key: 'service_name' },
    { title: t('OBJECTS'), key: 'num_objects', sorted: true },
    { title: t('SIZE'), key: 'usage', sorted: true },
    { title: t('CREATED_AT'), key: 'created_at', sorted: true },
    { title: '', key: 'props' },
  ]

  const actions = [
    {
      id: 'delete',
      title: t('DELETE'),
      icon: icons.DeleteIcon,
      divider: false,
      onClick: (bucket: StaffBucket) => {
        setSelectedBucket(bucket)
        setViewModal(true)
      },
    },
  ]

  return (
    <>
      <StartPage
        title={`${t('OBJECT_STORAGE')}`}
        noResourceText={`${t('NO_STAFF_BUCKETS')}`}
        haveResource={buckets.length > 0}
        pageLoading={pageLoading}
        withoutCreateResourceButton
        rightCustomCreateButton={(
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}><SearchComponent /></div>
            <Button
              variant="stroke"
              size="display"
              title={t('REFRESH')}
              loading={loading}
              onClick={() => loadBuckets()}
            />
          </div>
        )}
        tableData={{
          columns,
          data: buckets,
          props: actions,
          paginationData: pagination,
          updateData: loadBuckets,
          hideCheckbox: true,
          loading,
        }}
      />
      <ModalVariables
        modalType="delete"
        viewModal={viewModal}
        activeAnimation={viewModal}
        closeModal={closeModal}
        closeModalCancel={() => setViewModal(false)}
        alertTitle={t('BUCKET')}
        alertSubTitle={selectedBucket ? [selectedBucket] : []}
        instance={selectedBucket}
        loading={loading}
      />
    </>
  )
}

export default Buckets
